import { useMemo, useState } from 'react';

import {
    Box,
    Button,
    Dialog,
    DialogContent,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    styled,
    Typography,
} from '@mui/material';
import { removeAllWhitespace } from '@npi/buddy-utils';
import { Formik } from 'formik';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useMutation, useQuery } from '@redwoodjs/web';

import { CustomInput } from 'src/components/CustomInput';
import { PhoneNumberField } from 'src/components/FormikComponents';
import { DropDown } from 'src/components/FormikComponents/DropDown';
import Loader from 'src/components/Loader/Loader';
import ModalContent from 'src/components/ModalContent/ModalContent';
import { SearchInput } from 'src/components/SearchInput';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { ADD_USER_INITIAL_VALUES, DEFAULT_BORDER, Hierarchy, UserHierarchies } from 'src/constants';
import { ADD_USER_MUTATION } from 'src/graphql/mutations/createUser.mutation';
import { GET_BUSINESSES } from 'src/graphql/queries/businesses.query';
import { GET_ROLES } from 'src/graphql/queries/roles.query';
import { GET_PARTNER_OF_PARTNER } from 'src/graphql/queries/subpartners.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { enumToCamelCase, formatToInternational } from 'src/lib/formatters';
import { useEmailAddressValidation } from 'src/utils/validateEmail';

import { addUserValidationSchema } from './validation/addUserValidation';

type FormattedFormData = {
    email: string;
    phoneNumber: string;
    roleId: number;
    businessId?: number;
    partnerId?: number;
    subPartnerId?: number;
    firstName: string;
    lastName: string;
    countryCode: string;
};

interface HierarchyOption {
    value: string;
    label: string;
}

const Form = styled('form')({
    width: '100%',
});

const AddUserPage = () => {
    const hierarchyOptions: HierarchyOption[] = useMemo(() => {
        return Object.values(UserHierarchies).map((value) => {
            return {
                value,
                label: value,
            };
        });
    }, [UserHierarchies]);

    // State Management
    const [userRoles, setUserRoles] = useState([]);
    const [emailIsValid, setEmailIsValid] = useState<boolean>(true);
    const [partnerDropDownOptions, setPartnerDropDownOptions] = useState();
    const [partnerOfPartnerDropDownOptions, setpartnerOfPartnerDropDownOptions] = useState();
    const [merchantDropDownOptions, setMerchantDropDownOptions] = useState();
    const [openNotificationModal, setOpenNotificationModal] = useState<boolean>(false);
    const [formattedFormData, setFormattedFormData] = useState<FormattedFormData>();
    const [isPartnerOfPartnerSelected, setIsPartnerOfPartnerSelected] = useState(false);
    const [partnerLegalName, setPartnerLegalName] = useState('');
    const [partnerOfPartnerId, setPartnerOfPartnerId] = useState(null);
    const [fetchedPartnerId, setFetchedPartnerId] = useState(null);
    const [popBusinessId, setPopBusinessId] = useState(null);

    // Queries & Mutations
    const [createUserMutation, { loading: creatingNewUser }] = useMutation(ADD_USER_MUTATION, {
        onCompleted: () => {
            showSuccessToast('User Created Successfully');
            navigate('/user-management/users');
        },
        onError: () => {
            showErrorToast('Error Creating User');
        },
    });

    const { data: organisationData, loading: fetchingBusinesses } = useQuery(GET_BUSINESSES, {
        onError: () => {
            showErrorToast('Error fetching organisation list');
        },
    });

    const { data: allUserRoles, loading: fetchingRoles } = useQuery(GET_ROLES, {
        onError: () => {
            showErrorToast('Error fetching user roles');
        },
    });

    const formatFormData = (
        {
            email,
            mobileNumber,
            userRole,
            businessId,
            partnerId,
            merchant,
            partnerOfPartner,
            firstName,
            lastName,
            countryCode,
            sellerId,
            pipeDriveUserId,
            zenDeskUserId,
        },
        hierarchyLevel,
        fetchedPartnerId
    ) => {
        let finalPartnerId = partnerId;
        let finalBusinessId = businessId;

        if (hierarchyLevel === UserHierarchies.PARTNER_OF_PARTNER) {
            finalPartnerId = fetchedPartnerId;
            finalBusinessId = popBusinessId;
        }
        return {
            email,
            phoneNumber: formatToInternational(mobileNumber.toString(), countryCode),
            roleId: Number(userRole),
            businessId: finalBusinessId ? Number(finalBusinessId) : null,
            partnerId: finalPartnerId ? Number(finalPartnerId) : null,
            subPartnerId: partnerOfPartner ? Number(partnerOfPartnerId) : null,
            merchantId: merchant ? Number(merchant) : null,
            firstName,
            lastName,
            countryCode,
            sellerId,
            pipeDriveUserId,
            zenDeskUserId,
        };
    };

    const handleHierarchyDropDownChange = (selectedValue: string) => {
        const filteredRolesByHierarchy = allUserRoles?.roles?.filter((role) => {
            let { hierarchyLevel } = role;
            if (hierarchyLevel === Hierarchy.PARTNER_OF_PARTNER) {
                hierarchyLevel = UserHierarchies.PARTNER_OF_PARTNER.toUpperCase();
                return hierarchyLevel === selectedValue.toUpperCase();
            }
            return hierarchyLevel === selectedValue.toUpperCase();
        });

        setUserRoles(filteredRolesByHierarchy);

        if (selectedValue.replace(/\s+/g, '_').toUpperCase() === Hierarchy.PARTNER) {
            const partnerBusinesses = organisationData?.businesses.filter((business) => {
                return business.hierarchyLevel === Hierarchy.PARTNER;
            });
            setPartnerDropDownOptions(partnerBusinesses);
        }

        if (selectedValue.replace(/\s+/g, '_').toUpperCase() === Hierarchy.PARTNER_OF_PARTNER) {
            const partnerOfPartnerBusinesses = organisationData?.businesses.filter((business) => {
                return business.hierarchyLevel === Hierarchy.PARTNER_OF_PARTNER;
            });
            setpartnerOfPartnerDropDownOptions(partnerOfPartnerBusinesses);
            setIsPartnerOfPartnerSelected(true);
        }

        if (selectedValue.replace(/\s+/g, '_').toUpperCase() === Hierarchy.MERCHANT) {
            const merchantBusinesses = organisationData?.businesses.filter((business) => {
                return business.hierarchyLevel === Hierarchy.MERCHANT;
            });
            setMerchantDropDownOptions(merchantBusinesses);
        }
    };

    useQuery(GET_PARTNER_OF_PARTNER, {
        variables: { id: partnerOfPartnerId },
        skip: !partnerOfPartnerId,
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const fetchedLegalName = data.partnerOfPartner.partner.business.legalName;
            setPartnerLegalName(fetchedLegalName);
            const partnerIdFromQuery = data.partnerOfPartner.partner.id;
            setFetchedPartnerId(partnerIdFromQuery);
        },
    });

    // If the users role contains the permission to add a merchant, warn them when they dont supply a seller ID
    const handleCreateNewUser = async (values) => {
        const [businessId, partnerId, subPartnerId] = `${values.partner}`.split('-');
        values = { ...values, businessId, partnerId, subPartnerId };
        const formSubmission = formatFormData(values, values.hierarchyLevel, fetchedPartnerId);
        const selectedRole = userRoles.find((role) => {
            return role.id === formSubmission.roleId;
        });

        // Check if the new users role has the permission to add merchants & notify them if they haven't provided a sellerId
        if (selectedRole?.permissions[0]?.addMerchant && !formSubmission.sellerId) {
            setFormattedFormData(formSubmission);
            setOpenNotificationModal(true);
        } else {
            await createUserMutation({
                variables: {
                    input: {
                        ...formSubmission,
                        username: removeAllWhitespace(
                            `${formSubmission?.firstName}.${formSubmission?.lastName}`
                        ).toLowerCase(),
                    },
                },
            });
        }
    };
    const { isEmailAddressValid } = useEmailAddressValidation();
    const handleValidateEmail = async (email: string) => {
        const isEmailAValid = await isEmailAddressValid(email);
        if (isEmailAValid) {
            showSuccessToast('Email is valid');
            setEmailIsValid(true);
        } else {
            showErrorToast('Email is not valid');
            setEmailIsValid(false);
        }
    };

    return (
        <PageContent
            heading="Add User"
            subHeading="Create a new user and assign the correct permissions for their platform access"
        >
            <MetaTags title="AddUser" description="AddUser page" />
            {(creatingNewUser || fetchingBusinesses || fetchingRoles) && <Loader />}
            <Grid container>
                <Formik
                    initialValues={ADD_USER_INITIAL_VALUES}
                    validationSchema={addUserValidationSchema}
                    onSubmit={async (values) => {
                        await handleCreateNewUser(values);
                    }}
                >
                    {(formikProps) => {
                        const { values, setFieldValue, errors, handleChange, touched } =
                            formikProps;
                        return (
                            <Form>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                    {/* User Types */}
                                    <Grid
                                        item
                                        xs={12}
                                        sx={{
                                            ...DEFAULT_BORDER,
                                            backgroundColor: '#FFFFFF',
                                            boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                                            padding: '32px 24px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            gap: '24px',
                                        }}
                                        container
                                    >
                                        <Grid
                                            item
                                            xs={12}
                                            sx={{ borderBottom: '1px solid #F18F34' }}
                                        >
                                            <Box
                                                display="flex"
                                                flexDirection="column"
                                                gap="8px"
                                                marginBottom="8px"
                                            >
                                                <Typography variant="h2">User Types</Typography>
                                                <Typography>
                                                    Provide the user&apos;s details below
                                                </Typography>
                                            </Box>
                                        </Grid>

                                        {/* User Type Drop Down */}
                                        <Grid item xs={12} spacing={2} container>
                                            {/* Hierarchy Level */}
                                            <Grid item lg={6} xs={12}>
                                                <DropDown
                                                    name="hierarchyLevel"
                                                    label="Hierarchy Level"
                                                    onChange={(value: string) => {
                                                        setFieldValue('userRole', '');
                                                        handleHierarchyDropDownChange(value);
                                                    }}
                                                    options={hierarchyOptions}
                                                    placeholder="Select Hierarchy Level"
                                                />
                                            </Grid>
                                            {/* Partner & PoP */}
                                            {values.hierarchyLevel ===
                                                UserHierarchies.PARTNER_OF_PARTNER && (
                                                <Grid item lg={6} xs={12}>
                                                    <DropDown
                                                        name="partnerOfPartner"
                                                        label="Partner of Partner"
                                                        onChange={(value) => {
                                                            const selectedPoPBusinessId = value;

                                                            // Find the business data corresponding to the selected PoP ID
                                                            const businessData =
                                                                organisationData?.businesses.find(
                                                                    (business) => {
                                                                        return (
                                                                            business.id?.toString() ===
                                                                            selectedPoPBusinessId
                                                                        );
                                                                    }
                                                                );

                                                            const partnerOfPartnerId = Number(
                                                                businessData?.partnerOfPartnerId
                                                            );
                                                            setPopBusinessId(businessData?.id);

                                                            setPartnerOfPartnerId(
                                                                Number(partnerOfPartnerId)
                                                            );
                                                        }}
                                                        options={(
                                                            partnerOfPartnerDropDownOptions ?? []
                                                        ).map((organisation) => {
                                                            return {
                                                                value: organisation.id.toString(),
                                                                label: organisation.legalName,
                                                            };
                                                        })}
                                                        placeholder="Select Partner of Partner"
                                                    />

                                                    {isPartnerOfPartnerSelected && (
                                                        <CustomInput
                                                            name="partnerLegalName"
                                                            label="Partner"
                                                            placeholder="Please select a Partner Of Partner above"
                                                            value={partnerLegalName}
                                                            disabled
                                                        />
                                                    )}
                                                </Grid>
                                            )}

                                            {values.hierarchyLevel === UserHierarchies.MERCHANT && (
                                                <Grid item lg={6} xs={12}>
                                                    <DropDown
                                                        name="merchant"
                                                        label="Merchant"
                                                        options={(
                                                            merchantDropDownOptions ?? []
                                                        ).map((organisation) => {
                                                            return {
                                                                value: organisation.id.toString(),
                                                                label: organisation.legalName,
                                                            };
                                                        })}
                                                        placeholder="Select Merchant"
                                                    />
                                                </Grid>
                                            )}

                                            {values.hierarchyLevel === UserHierarchies.PARTNER && (
                                                <Grid item lg={6} xs={12}>
                                                    <DropDown
                                                        name="partner"
                                                        label="Partner"
                                                        options={(partnerDropDownOptions ?? []).map(
                                                            (organisation) => {
                                                                return {
                                                                    value: `${organisation.id}-${organisation.partnerId}`,
                                                                    label: organisation.legalName,
                                                                };
                                                            }
                                                        )}
                                                        placeholder="Select Partner"
                                                    />
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Grid>

                                    {/* User Details Form */}
                                    {values.hierarchyLevel?.length > 0 && (
                                        <Grid
                                            item
                                            xs={12}
                                            sx={{
                                                ...DEFAULT_BORDER,
                                                backgroundColor: '#FFFFFF',
                                                boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                                                padding: '32px 24px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                gap: '24px',
                                            }}
                                            container
                                        >
                                            <Grid
                                                item
                                                xs={12}
                                                sx={{ borderBottom: '1px solid #F18F34' }}
                                            >
                                                <Box
                                                    display="flex"
                                                    flexDirection="column"
                                                    gap="8px"
                                                    marginBottom="8px"
                                                >
                                                    <Typography variant="h2">
                                                        User Details
                                                    </Typography>
                                                    <Typography>
                                                        Add new user details in this section
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12} spacing={2} container>
                                                {/* First Name Input */}
                                                <Grid item xs={12} lg={6}>
                                                    <CustomInput
                                                        name="firstName"
                                                        label="First Name"
                                                        onChange={(event) => {
                                                            setFieldValue(
                                                                'firstName',
                                                                event.target.value?.trim()
                                                            );
                                                        }}
                                                        value={values.firstName}
                                                        error={errors.firstName}
                                                    />
                                                </Grid>
                                                {/* Last Name Input */}
                                                <Grid item xs={12} lg={6}>
                                                    <CustomInput
                                                        name="lastName"
                                                        label="Last Name"
                                                        onChange={(event) => {
                                                            setFieldValue(
                                                                'lastName',
                                                                event.target.value?.trim()
                                                            );
                                                        }}
                                                        value={values.lastName}
                                                        error={errors.lastName}
                                                    />
                                                </Grid>
                                                {/* Mobile Number Input */}
                                                <Grid item xs={12} lg={6}>
                                                    <PhoneNumberField
                                                        {...formikProps}
                                                        formikFieldName={'mobileNumber'}
                                                    />
                                                </Grid>
                                                {/* Email Input */}
                                                <Grid item xs={12} lg={6}>
                                                    <SearchInput
                                                        name="email"
                                                        label="Email Address"
                                                        value={values.email}
                                                        onChange={(e) => {
                                                            handleChange(e);
                                                        }}
                                                        error={
                                                            errors.email &&
                                                            (!emailIsValid || !!errors.email)
                                                                ? errors.email || 'Email is Invalid'
                                                                : null
                                                        }
                                                        boxStyling={{ width: '100%' }}
                                                        searchText="Validate Email"
                                                        onSearch={handleValidateEmail}
                                                    />
                                                </Grid>
                                                {/* User Role Drop Down */}
                                                <Grid item xs={12} lg={6}>
                                                    <DropDown
                                                        placeholder="Select User Role"
                                                        label="User Role"
                                                        name="userRole"
                                                        options={userRoles?.map((userRole) => {
                                                            return {
                                                                label: enumToCamelCase(
                                                                    userRole.name
                                                                ),
                                                                value: userRole.id.toString(),
                                                            };
                                                        })}
                                                        disabled={values.hierarchyLevel === ''}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    )}

                                    {/* Configuration Section */}
                                    {values.hierarchyLevel !== '' && (
                                        <Grid
                                            item
                                            xs={12}
                                            sx={{
                                                ...DEFAULT_BORDER,
                                                backgroundColor: '#FFFFFF',
                                                boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                                                padding: '32px 24px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                gap: '24px',
                                            }}
                                            container
                                        >
                                            <Grid
                                                item
                                                xs={12}
                                                display="flex"
                                                justifyContent="space-between"
                                                sx={{ borderBottom: '1px solid #F18F34' }}
                                            >
                                                <Box
                                                    display="flex"
                                                    flexDirection="column"
                                                    gap="8px"
                                                    marginBottom="8px"
                                                >
                                                    <Typography variant="h2">
                                                        Configuration
                                                    </Typography>
                                                    <Typography>
                                                        Set user configuration in this section
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            {/* Configuration Form */}
                                            <Grid item xs={12} spacing={2} container>
                                                {/* WorldPay  */}
                                                <Grid item xs={12} lg={6}>
                                                    <Grid
                                                        item
                                                        xs={12}
                                                        lg={6}
                                                        container
                                                        direction="row"
                                                        alignItems="center"
                                                        gap="8px"
                                                    >
                                                        <Grid item>
                                                            <FormLabel
                                                                id="world-pay-seller"
                                                                sx={{ color: '#434341' }}
                                                            >
                                                                WorldPay Seller?
                                                            </FormLabel>
                                                        </Grid>
                                                        <Grid item>
                                                            <FormControl component="fieldset">
                                                                <RadioGroup
                                                                    aria-labelledby="worldPaySeller"
                                                                    defaultValue={false}
                                                                    name="worldPaySeller"
                                                                    row
                                                                    onChange={(event) => {
                                                                        setFieldValue(
                                                                            'worldPaySeller',
                                                                            JSON.parse(
                                                                                event.target.value
                                                                            )
                                                                        );
                                                                    }}
                                                                >
                                                                    <FormControlLabel
                                                                        value
                                                                        control={<Radio />}
                                                                        label="Yes"
                                                                    />
                                                                    <FormControlLabel
                                                                        value={false}
                                                                        control={<Radio />}
                                                                        label="No"
                                                                    />
                                                                </RadioGroup>
                                                            </FormControl>
                                                        </Grid>
                                                    </Grid>
                                                    {values.worldPaySeller && (
                                                        <Grid item>
                                                            <CustomInput
                                                                name="sellerId"
                                                                label="Seller ID"
                                                                onChange={handleChange}
                                                                value={values.sellerId}
                                                                error={errors.sellerId}
                                                            />
                                                        </Grid>
                                                    )}
                                                </Grid>

                                                {/* Pipe Drive  */}
                                                <Grid item xs={12} lg={6}>
                                                    <Grid
                                                        item
                                                        xs={12}
                                                        lg={6}
                                                        container
                                                        direction="row"
                                                        alignItems="center"
                                                        gap="8px"
                                                    >
                                                        <Grid item>
                                                            <FormLabel
                                                                id="pipe-drive"
                                                                sx={{ color: '#434341' }}
                                                            >
                                                                Pipe Drive?
                                                            </FormLabel>
                                                        </Grid>
                                                        <Grid item>
                                                            <FormControl>
                                                                <RadioGroup
                                                                    aria-labelledby="pipeDrive"
                                                                    defaultValue={false}
                                                                    name="pipeDrive"
                                                                    row
                                                                    onChange={(event) => {
                                                                        setFieldValue(
                                                                            'pipeDrive',
                                                                            JSON.parse(
                                                                                event.target.value
                                                                            )
                                                                        );
                                                                    }}
                                                                >
                                                                    <FormControlLabel
                                                                        value
                                                                        control={<Radio />}
                                                                        label="Yes"
                                                                    />
                                                                    <FormControlLabel
                                                                        value={false}
                                                                        control={<Radio />}
                                                                        label="No"
                                                                    />
                                                                </RadioGroup>
                                                            </FormControl>
                                                        </Grid>
                                                    </Grid>
                                                    {values.pipeDrive && (
                                                        <CustomInput
                                                            name="pipeDriveUserId"
                                                            label="Pipe Drive User ID"
                                                            onChange={handleChange}
                                                            value={values.pipeDriveUserId}
                                                            error={errors.pipeDriveUserId}
                                                        />
                                                    )}
                                                </Grid>
                                                {/* Technical Support Access  */}
                                                <Grid item xs={12} lg={6}>
                                                    <Grid
                                                        item
                                                        container
                                                        direction="row"
                                                        alignItems="center"
                                                        gap="8px"
                                                    >
                                                        <Grid item>
                                                            <FormLabel
                                                                id="technicalSupportAccess"
                                                                sx={{ color: '#434341' }}
                                                            >
                                                                Technical Support Access?
                                                            </FormLabel>
                                                        </Grid>
                                                        <Grid item>
                                                            <FormControl sx={{ display: 'flex' }}>
                                                                <RadioGroup
                                                                    aria-labelledby="technicalSupportAccess"
                                                                    defaultValue={false}
                                                                    name="technicalSupportAccess"
                                                                    row
                                                                    onChange={(event) => {
                                                                        setFieldValue(
                                                                            'technicalSupportAccess',
                                                                            JSON.parse(
                                                                                event.target.value
                                                                            )
                                                                        );
                                                                    }}
                                                                >
                                                                    <FormControlLabel
                                                                        value
                                                                        control={<Radio />}
                                                                        label="Yes"
                                                                    />
                                                                    <FormControlLabel
                                                                        value={false}
                                                                        control={<Radio />}
                                                                        label="No"
                                                                    />
                                                                </RadioGroup>
                                                            </FormControl>
                                                        </Grid>
                                                    </Grid>
                                                    {values.technicalSupportAccess && (
                                                        <CustomInput
                                                            name="zenDeskUserId"
                                                            label="ZenDesk User ID"
                                                            onChange={handleChange}
                                                            value={values.zenDeskUserId}
                                                            error={errors.zenDeskUserId}
                                                        />
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    )}

                                    {/* Submit Buttons */}
                                    {values.hierarchyLevel !== '' && (
                                        <Grid
                                            item
                                            xs={12}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                                gap: '8px',
                                            }}
                                        >
                                            <Button
                                                variant="outlined"
                                                sx={{
                                                    color: 'black',
                                                    borderColor: 'black',
                                                    '&:hover': {
                                                        borderColor: 'rgba(29, 29, 27, 0.5)',
                                                        color: 'rgba(29, 29, 27, 0.5)',
                                                        backgroundColor: 'none',
                                                    },
                                                    width: '146px',
                                                }}
                                                onClick={() => {
                                                    return navigate('/user-management/users');
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="main"
                                                type="submit"
                                                sx={{ width: '146px' }}
                                                onClick={(e) => {
                                                    e.preventDefault();

                                                    if (Object.keys(errors).length > 0) {
                                                        showErrorToast(
                                                            'Please fill in required fields'
                                                        );
                                                    } else {
                                                        handleCreateNewUser(values);
                                                    }
                                                }}
                                            >
                                                Add User
                                            </Button>
                                        </Grid>
                                    )}
                                </Box>
                            </Form>
                        );
                    }}
                </Formik>
            </Grid>
            <Dialog
                open={openNotificationModal}
                onClose={() => {
                    return setOpenNotificationModal(false);
                }}
                aria-labelledby="notification-modal"
                sx={{
                    '& .MuiDialog-paper': {
                        width: '400px',
                    },
                }}
            >
                <DialogContent>
                    <ModalContent
                        handleClose={() => {
                            return setOpenNotificationModal(false);
                        }}
                        handleAction={async () => {
                            return await createUserMutation({
                                variables: {
                                    input: {
                                        ...formattedFormData,
                                        username: removeAllWhitespace(
                                            `${formattedFormData?.firstName}.${formattedFormData?.lastName}`
                                        ).toLowerCase(),
                                    },
                                },
                            });
                        }}
                        header="NOTE"
                        message="This user cannot add a Merchant as a Seller ID has not been provided"
                        buttonText="Confirm"
                    />
                </DialogContent>
            </Dialog>
        </PageContent>
    );
};

export default AddUserPage;
